import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X, ArrowUp, ArrowDown, Home } from 'lucide-react';

interface CMSPage {
  id: string;
  slug: string;
  title: string;
  meta_description: string | null;
  meta_keywords: string | null;
  is_published: boolean;
  is_homepage: boolean;
  created_at: string;
  updated_at: string;
}

interface CMSSection {
  id: string;
  page_id: string;
  section_type: 'hero' | 'features' | 'testimonials' | 'cta' | 'stats' | 'pricing' | 'faq' | 'custom';
  title: string | null;
  content: any;
  display_order: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export default function CMSManagement() {
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<CMSPage | null>(null);
  const [sections, setSections] = useState<CMSSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPageModal, setShowPageModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<CMSSection | null>(null);
  const [pageForm, setPageForm] = useState({
    slug: '',
    title: '',
    meta_description: '',
    meta_keywords: '',
    is_published: false,
    is_homepage: false,
  });

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      loadSections(selectedPage.id);
    }
  }, [selectedPage]);

  const loadPages = async () => {
    try {
      const { data, error } = await supabase
        .from('cms_pages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPages(data || []);
    } catch (error: any) {
      alert('Erreur lors du chargement des pages: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async (pageId: string) => {
    try {
      const { data, error } = await supabase
        .from('cms_sections')
        .select('*')
        .eq('page_id', pageId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setSections(data || []);
    } catch (error: any) {
      alert('Erreur lors du chargement des sections: ' + error.message);
    }
  };

  const handleCreatePage = () => {
    setPageForm({
      slug: '',
      title: '',
      meta_description: '',
      meta_keywords: '',
      is_published: false,
      is_homepage: false,
    });
    setShowPageModal(true);
  };

  const handleEditPage = (page: CMSPage) => {
    setPageForm({
      slug: page.slug,
      title: page.title,
      meta_description: page.meta_description || '',
      meta_keywords: page.meta_keywords || '',
      is_published: page.is_published,
      is_homepage: page.is_homepage,
    });
    setSelectedPage(page);
    setShowPageModal(true);
  };

  const handleSavePage = async () => {
    try {
      if (selectedPage) {
        // Update existing page
        const { error } = await supabase
          .from('cms_pages')
          .update({
            ...pageForm,
            updated_by: (await supabase.auth.getUser()).data.user?.id,
          })
          .eq('id', selectedPage.id);

        if (error) throw error;
      } else {
        // Create new page
        const { error } = await supabase
          .from('cms_pages')
          .insert({
            ...pageForm,
            created_by: (await supabase.auth.getUser()).data.user?.id,
          });

        if (error) throw error;
      }

      setShowPageModal(false);
      setSelectedPage(null);
      loadPages();
    } catch (error: any) {
      alert('Erreur lors de la sauvegarde: ' + error.message);
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette page ?')) return;

    try {
      const { error } = await supabase
        .from('cms_pages')
        .delete()
        .eq('id', pageId);

      if (error) throw error;

      if (selectedPage?.id === pageId) {
        setSelectedPage(null);
        setSections([]);
      }
      loadPages();
    } catch (error: any) {
      alert('Erreur lors de la suppression: ' + error.message);
    }
  };

  const handleTogglePublish = async (page: CMSPage) => {
    try {
      const { error } = await supabase
        .from('cms_pages')
        .update({ is_published: !page.is_published })
        .eq('id', page.id);

      if (error) throw error;
      loadPages();
    } catch (error: any) {
      alert('Erreur lors de la mise à jour: ' + error.message);
    }
  };

  const handleSelectPage = (page: CMSPage) => {
    setSelectedPage(page);
  };

  const handleCreateSection = () => {
    setEditingSection(null);
    setShowSectionModal(true);
  };

  const handleEditSection = (section: CMSSection) => {
    setEditingSection(section);
    setShowSectionModal(true);
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette section ?')) return;

    try {
      const { error } = await supabase
        .from('cms_sections')
        .delete()
        .eq('id', sectionId);

      if (error) throw error;
      if (selectedPage) loadSections(selectedPage.id);
    } catch (error: any) {
      alert('Erreur lors de la suppression: ' + error.message);
    }
  };

  const handleToggleSectionVisibility = async (section: CMSSection) => {
    try {
      const { error } = await supabase
        .from('cms_sections')
        .update({ is_visible: !section.is_visible })
        .eq('id', section.id);

      if (error) throw error;
      if (selectedPage) loadSections(selectedPage.id);
    } catch (error: any) {
      alert('Erreur lors de la mise à jour: ' + error.message);
    }
  };

  const handleMoveSectionUp = async (section: CMSSection, index: number) => {
    if (index === 0) return;

    const prevSection = sections[index - 1];
    try {
      await supabase
        .from('cms_sections')
        .update({ display_order: prevSection.display_order })
        .eq('id', section.id);

      await supabase
        .from('cms_sections')
        .update({ display_order: section.display_order })
        .eq('id', prevSection.id);

      if (selectedPage) loadSections(selectedPage.id);
    } catch (error: any) {
      alert('Erreur lors du déplacement: ' + error.message);
    }
  };

  const handleMoveSectionDown = async (section: CMSSection, index: number) => {
    if (index === sections.length - 1) return;

    const nextSection = sections[index + 1];
    try {
      await supabase
        .from('cms_sections')
        .update({ display_order: nextSection.display_order })
        .eq('id', section.id);

      await supabase
        .from('cms_sections')
        .update({ display_order: section.display_order })
        .eq('id', nextSection.id);

      if (selectedPage) loadSections(selectedPage.id);
    } catch (error: any) {
      alert('Erreur lors du déplacement: ' + error.message);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Gestion du Contenu (CMS)</h2>
        <button
          onClick={handleCreatePage}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Nouvelle Page
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des pages */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900">Pages</h3>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {pages.map((page) => (
              <div
                key={page.id}
                className={`p-4 cursor-pointer hover:bg-gray-50 ${
                  selectedPage?.id === page.id ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleSelectPage(page)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{page.title}</h4>
                      {page.is_homepage && (
                        <Home className="w-4 h-4 text-blue-600" title="Page d'accueil" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500">/{page.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePublish(page);
                    }}
                    className={`p-1 rounded ${
                      page.is_published
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={page.is_published ? 'Publié' : 'Brouillon'}
                  >
                    {page.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPage(page);
                    }}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePage(page.id);
                    }}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sections de la page sélectionnée */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          {selectedPage ? (
            <>
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Sections: {selectedPage.title}</h3>
                  <p className="text-sm text-gray-500">Gérez les sections de cette page</p>
                </div>
                <button
                  onClick={handleCreateSection}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter Section
                </button>
              </div>
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {sections.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    Aucune section. Cliquez sur "Ajouter Section" pour commencer.
                  </div>
                ) : (
                  sections.map((section, index) => (
                    <div key={section.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                              {section.section_type}
                            </span>
                            {!section.is_visible && (
                              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                                Masqué
                              </span>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900">
                            {section.title || 'Sans titre'}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            Ordre: {section.display_order}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleMoveSectionUp(section, index)}
                            disabled={index === 0}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMoveSectionDown(section, index)}
                            disabled={index === sections.length - 1}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleSectionVisibility(section)}
                            className={`p-1 rounded ${
                              section.is_visible
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                          >
                            {section.is_visible ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEditSection(section)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSection(section.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              Sélectionnez une page pour gérer ses sections
            </div>
          )}
        </div>
      </div>

      {/* Modal Page */}
      {showPageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedPage ? 'Modifier la page' : 'Nouvelle page'}
              </h3>
              <button
                onClick={() => {
                  setShowPageModal(false);
                  setSelectedPage(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre de la page *
                </label>
                <input
                  type="text"
                  value={pageForm.title}
                  onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  value={pageForm.slug}
                  onChange={(e) =>
                    setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="mon-slug-url"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  L'URL sera: /{pageForm.slug || 'slug'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meta Description (SEO)
                </label>
                <textarea
                  value={pageForm.meta_description}
                  onChange={(e) => setPageForm({ ...pageForm, meta_description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mots-clés (SEO)
                </label>
                <input
                  type="text"
                  value={pageForm.meta_keywords}
                  onChange={(e) => setPageForm({ ...pageForm, meta_keywords: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="mot1, mot2, mot3"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={pageForm.is_published}
                    onChange={(e) => setPageForm({ ...pageForm, is_published: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Publier la page</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={pageForm.is_homepage}
                    onChange={(e) => setPageForm({ ...pageForm, is_homepage: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Page d'accueil</span>
                </label>
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPageModal(false);
                  setSelectedPage(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSavePage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Section */}
      {showSectionModal && selectedPage && (
        <SectionModal
          pageId={selectedPage.id}
          section={editingSection}
          onClose={() => {
            setShowSectionModal(false);
            setEditingSection(null);
          }}
          onSave={() => {
            setShowSectionModal(false);
            setEditingSection(null);
            loadSections(selectedPage.id);
          }}
        />
      )}
    </div>
  );
}

// Section Modal Component
interface SectionModalProps {
  pageId: string;
  section: CMSSection | null;
  onClose: () => void;
  onSave: () => void;
}

function SectionModal({ pageId, section, onClose, onSave }: SectionModalProps) {
  const [sectionForm, setSectionForm] = useState({
    section_type: section?.section_type || 'hero' as CMSSection['section_type'],
    title: section?.title || '',
    content: JSON.stringify(section?.content || {}, null, 2),
    display_order: section?.display_order || 0,
    is_visible: section?.is_visible ?? true,
  });

  const handleSave = async () => {
    try {
      let contentJson;
      try {
        contentJson = JSON.parse(sectionForm.content);
      } catch {
        alert('Le contenu JSON est invalide');
        return;
      }

      const data = {
        page_id: pageId,
        section_type: sectionForm.section_type,
        title: sectionForm.title || null,
        content: contentJson,
        display_order: sectionForm.display_order,
        is_visible: sectionForm.is_visible,
      };

      if (section) {
        const { error } = await supabase
          .from('cms_sections')
          .update(data)
          .eq('id', section.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('cms_sections').insert(data);
        if (error) throw error;
      }

      onSave();
    } catch (error: any) {
      alert('Erreur lors de la sauvegarde: ' + error.message);
    }
  };

  const sectionTypes = [
    { value: 'hero', label: 'Hero (En-tête)' },
    { value: 'features', label: 'Fonctionnalités' },
    { value: 'testimonials', label: 'Témoignages' },
    { value: 'cta', label: 'Appel à l\'action' },
    { value: 'stats', label: 'Statistiques' },
    { value: 'pricing', label: 'Tarification' },
    { value: 'faq', label: 'FAQ' },
    { value: 'custom', label: 'Personnalisé' },
  ];

  const contentExamples: Record<string, string> = {
    hero: JSON.stringify({
      heading: "Gérez votre trésorerie en toute simplicité",
      subheading: "Solution complète pour associations et établissements",
      cta_text: "Commencer gratuitement",
      cta_url: "#beta-access"
    }, null, 2),
    features: JSON.stringify({
      items: [
        {
          icon: "Wallet",
          title: "Suivi en temps réel",
          description: "Visualisez votre trésorerie instantanément"
        },
        {
          icon: "TrendingUp",
          title: "Prévisions intelligentes",
          description: "Anticipez vos besoins futurs"
        }
      ]
    }, null, 2),
    stats: JSON.stringify({
      items: [
        { value: "500+", label: "Établissements", description: "Nous font confiance" },
        { value: "99.9%", label: "Disponibilité", description: "Service fiable" }
      ]
    }, null, 2),
    cta: JSON.stringify({
      title: "Prêt à démarrer ?",
      description: "Rejoignez-nous aujourd'hui",
      button_text: "Inscription gratuite",
      button_url: "#beta-access"
    }, null, 2),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {section ? 'Modifier la section' : 'Nouvelle section'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de section *
            </label>
            <select
              value={sectionForm.section_type}
              onChange={(e) => {
                const type = e.target.value as CMSSection['section_type'];
                setSectionForm({
                  ...sectionForm,
                  section_type: type,
                  content: contentExamples[type] || sectionForm.content,
                });
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {sectionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre (optionnel)</label>
            <input
              type="text"
              value={sectionForm.title}
              onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contenu (JSON) *
            </label>
            <textarea
              value={sectionForm.content}
              onChange={(e) => setSectionForm({ ...sectionForm, content: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              rows={15}
              placeholder='{"key": "value"}'
            />
            <p className="text-xs text-gray-500 mt-1">Format JSON valide requis</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
            <input
              type="number"
              value={sectionForm.display_order}
              onChange={(e) =>
                setSectionForm({ ...sectionForm, display_order: parseInt(e.target.value) || 0 })
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={sectionForm.is_visible}
              onChange={(e) => setSectionForm({ ...sectionForm, is_visible: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm font-medium text-gray-700">Section visible</span>
          </label>
        </div>
        <div className="p-6 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
